import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useQuery } from '@apollo/client'
import { Popover } from 'antd'
import { InfoCircleTwoTone } from '@ant-design/icons'
import PromoCodeSelect from './PromoCodeSelect'
import {
  DomiciliationType,
  EstimationType,
  PurchaseType,
} from '../../../../types'
import { ESTIMATION_QUERY } from '../../../../graphql/Estimation'
import CentredLoader from '../../../../components/CentredLoader'

interface EstimationData {
  estimation: EstimationType
}

interface EstimationVars {
  domiciliationId: number
  paymentType: string
  promoCodeId?: number
}

interface Props {
  onChange: any
  paymentType: string
  domiciliation: DomiciliationType
  estimation?: EstimationType
  estimationVariables: any
}

const Estimation = (props: Props): JSX.Element => {
  const [variables, setVariables] = useState({
    domiciliationId: props.domiciliation.id,
    paymentType: props.paymentType,
    promoCodeId:
      props.estimation?.promoCode?.id || props.domiciliation.promoCode?.id,
    ...props.estimationVariables,
  })

  const {
    data: estimationData,
    loading: estimationLoading,
    refetch: estimationRefetch,
  } = useQuery<EstimationData, EstimationVars>(ESTIMATION_QUERY, {
    skip: !variables,
    fetchPolicy: 'no-cache',
    variables: variables,
  })

  useEffect(() => {
    if (variables) estimationRefetch()
  }, [variables])

  useEffect(() => {
    if (estimationData && estimationData.estimation) {
      props.onChange(estimationData.estimation)
    }
  }, [estimationData])

  const setEstimationPromoCode = (promoCodeId: number) => {
    return setVariables({ ...variables, promoCodeId: promoCodeId })
  }

  if (estimationLoading) return <CentredLoader />
  const { estimation } = estimationData!

  return (
    <Wrapper>
      {estimation && (
        <React.Fragment>
          <PromoCodeSelect
            defaultPromoCodeId={estimation.promoCode?.id}
            onChange={setEstimationPromoCode}
            paymentType={props.paymentType}
            addressId={props.estimationVariables.addressId}
            legalStatusId={props.estimationVariables.legalStatusId}
          />
          {estimation.pricing.purchases.length > 0 && (
            <div className="prices">
              {estimation.pricing.purchases.map((purchase: PurchaseType) => {
                return (
                  <div key={purchase.prettyName}>
                    <label>{purchase.prettyName}</label>
                    <span className="price">{purchase.amount! / 100}€</span>
                  </div>
                )
              })}
            </div>
          )}
          <div className="prices">
            <div className={`total ${estimation.paymentType}-color`}>
              <label className="total">Total HT</label>
              <span className="price">
                {estimation.pricing.totalPrice / 100}€
              </span>
            </div>
            <div className="total">
              <label className="total">Total TTC</label>
              <span className="price">
                {estimation.pricing.totalPriceVat / 100}€
              </span>
            </div>
          </div>
        </React.Fragment>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .total {
    font-weight: bold;
  }

  .prices {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  .price {
    float: right;
  }

  .domiciliation-line {
    height: 27px;
  }

  .monthly-color {
    color: #2dcfb3;
  }

  .yearly-color {
    color: #ffc36f;
  }

  .two_yearly-color {
    color: #ff3465;
  }

  .three_yearly-color {
    color: #003465;
  }

  label {
    margin-right: 5px;
  }
`

export default Estimation
